import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import Logo from '@/components/generals/logo'
import { NAV_LINKS } from '@/constants/site'

const isActive = (pathname: string, href: string): boolean => {
    if (href === '/') return pathname === '/'
    return pathname === href || pathname.startsWith(`${href}/`)
}

const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const close = () => setOpen(false)
        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    useEffect(() => {
        if (!open) return

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        window.addEventListener('keydown', onKeyDown)

        return () => window.removeEventListener('keydown', onKeyDown)
    }, [open])

    return (
        <header className='sticky top-0 z-40 border-b border-border/70 bg-background/85 backdrop-blur-md'>
            <div className='mx-auto flex h-16 max-w-6xl items-center justify-between gap-6 px-5 sm:px-8'>
                <Logo />

                <nav aria-label='Main' className='hidden items-center gap-1 md:flex'>
                    {NAV_LINKS.map(link => (
                        <Link
                            key={link.href}
                            href={link.href}
                            aria-current={isActive(router.pathname, link.href) ? 'page' : undefined}
                            className={cn(
                                'rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/40',
                                isActive(router.pathname, link.href) && 'text-foreground'
                            )}
                        >
                            {link.label}
                        </Link>
                    ))}
                </nav>

                <div className='flex items-center gap-2'>
                    <Link
                        href='/contact'
                        className={cn(buttonVariants({ size: 'sm' }), 'hidden sm:inline-flex')}
                    >
                        Get in touch
                    </Link>

                    <button
                        type='button'
                        onClick={() => setOpen(value => !value)}
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        aria-label={open ? 'Close menu' : 'Open menu'}
                        className={cn(buttonVariants({ variant: 'ghost', size: 'icon-sm' }), 'md:hidden')}
                    >
                        {open ? <X className='size-5' /> : <Menu className='size-5' />}
                    </button>
                </div>
            </div>

            {open && (
                <nav
                    id='mobile-menu'
                    aria-label='Mobile'
                    className='animate-in fade-in slide-in-from-top-2 border-t border-border/70 bg-background px-5 pt-3 pb-6 duration-200 md:hidden'
                >
                    <ul className='flex flex-col'>
                        {NAV_LINKS.map(link => (
                            <li key={link.href}>
                                <Link
                                    href={link.href}
                                    aria-current={isActive(router.pathname, link.href) ? 'page' : undefined}
                                    className={cn(
                                        'block rounded-md px-2 py-3 text-base font-medium text-muted-foreground',
                                        isActive(router.pathname, link.href) && 'text-foreground'
                                    )}
                                >
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>

                    <Link
                        href='/contact'
                        className={cn(buttonVariants(), 'mt-4 w-full')}
                    >
                        Get in touch
                    </Link>
                </nav>
            )}
        </header>
    )
}

export default SiteHeader
